import DOMPurify from 'dompurify'
import { marked } from 'marked'
import { slugifyHeading } from './blogUtils'

/**
 * Shift every rendered heading down one level (h1→h2, …, h6→h6) so the page
 * can keep a single document `<h1>` from React without duplicate top-level headings.
 */
export function demoteMarkdownHeadingLevels(html: string): string {
  if (typeof DOMParser === 'undefined') {
    return html
  }
  const doc = new DOMParser().parseFromString(
    `<div id="blog-md-wrap">${html}</div>`,
    'text/html'
  )
  const wrap = doc.getElementById('blog-md-wrap')
  if (!wrap) {
    return html
  }
  for (let level = 6; level >= 1; level -= 1) {
    const next = Math.min(level + 1, 6)
    if (next === level) {
      continue
    }
    wrap.querySelectorAll(`h${level}`).forEach((el) => {
      const h = doc.createElement(`h${next}`)
      h.innerHTML = el.innerHTML
      for (const attr of Array.from(el.attributes)) {
        h.setAttribute(attr.name, attr.value)
      }
      el.replaceWith(h)
    })
  }
  return wrap.innerHTML
}

export interface ParsedFrontmatter {
  data: Record<string, string>
  body: string
}

/**
 * Minimal YAML frontmatter (key: value lines). Values may be single- or double-quoted.
 */
export function parseFrontmatter(raw: string): ParsedFrontmatter {
  if (!raw.startsWith('---\n')) {
    return { data: {}, body: raw.trimStart() }
  }
  const close = raw.indexOf('\n---\n', 4)
  if (close === -1) {
    return { data: {}, body: raw }
  }
  const fmText = raw.slice(4, close)
  const body = raw.slice(close + 5)
  const data: Record<string, string> = {}
  for (const line of fmText.split('\n')) {
    const m = line.match(/^([\w-]+)\s*:\s*(.*)$/)
    if (!m) continue
    let v = m[2].trim()
    if (
      (v.startsWith('"') && v.endsWith('"')) ||
      (v.startsWith("'") && v.endsWith("'"))
    ) {
      v = v.slice(1, -1)
    }
    data[m[1]] = v
  }
  return { data, body: body.trimStart() }
}

/** Derive URL slug from a Vite glob key such as `/src/content/blog/welcome.md`. */
export function pathToSlugFromGlobKey(path: string): string {
  const m = path.match(/([^/]+)\.md$/)
  return m ? m[1] : ''
}

/**
 * Configure marked once at module load to inject `id` attributes on headings.
 * The id is slugified from the heading text so TOC links and scroll-spy work.
 * Calling marked.use() at module level (not per-render) avoids stacking
 * the renderer extension on every invocation.
 */
marked.use({
  renderer: {
    heading({ text, depth }: { text: string; depth: number }): string {
      const id = slugifyHeading(text.replace(/<[^>]+>/g, ''))
      return `<h${depth} id="${id}">${text}</h${depth}>\n`
    },
  },
})

export function renderMarkdownToSafeHtml(markdown: string): string {
  const html = marked.parse(markdown, { async: false })
  if (typeof html !== 'string') {
    throw new Error('Unexpected async markdown render')
  }
  const shifted = demoteMarkdownHeadingLevels(html)
  // Allow id attributes on headings so TOC links work
  return DOMPurify.sanitize(shifted, { ADD_ATTR: ['id'] })
}
